/**
 * Exact monetary arithmetic.
 *
 * The spec chose Python partly for `decimal`. We get the same guarantee in TypeScript
 * by never letting a currency value touch a float: amounts travel as decimal *strings*
 * (which is exactly how Postgres NUMERIC(12,2) serialises) and all arithmetic runs on
 * integer cents. `0.1 + 0.2` is structurally unrepresentable here.
 */

import { diffInDays, type IsoDate } from './dates.js';

export type MoneyString = string;

const SCALE = 2;
const SCALE_FACTOR = 100n;

/**
 * What someone typed → the wire format, or `undefined` while it is not yet a number.
 *
 * Every amount this app *renders* carries a Greek decimal comma — «87,45 €» — and every amount
 * it *accepts* is an invariant decimal point. Anywhere those two meet through a plain text input
 * the office loses: the separator on their keyboard, and on their own screen, is the one the
 * parser rejects. On 22/09/2026 that surfaced as the renewal dialog blanking itself — «59,5»
 * reached the server, `Money.Parse` threw, and the preview came back an error the dialog had no
 * branch for.
 *
 * The rule when both separators appear is the one a reader would apply: a comma is the decimal
 * point, so any dots before it are thousands and come out. With no comma the dot keeps its wire
 * meaning, because that is also what the field was pre-filled with.
 *
 * `undefined` for a value still being typed — «59.», «59,», «-» — rather than a throw or a zero.
 * Those are states a field legitimately passes through on the way to «59,50», and neither a
 * crash nor a silent 0,00 € is the right answer to a half-typed number.
 */
export function normaliseAmountInput(raw: string): MoneyString | undefined {
  const trimmed = raw.trim().replace(/\s|\u00a0/g, '');
  if (!trimmed) return undefined;

  const normalised = trimmed.includes(',')
    ? trimmed.replace(/\./g, '').replace(',', '.')
    : trimmed;

  return /^-?\d+(\.\d+)?$/.test(normalised) ? normalised : undefined;
}

/** Parses a decimal string into integer cents. Throws on anything unparseable. */
export function toCents(value: MoneyString | number): bigint {
  const str = typeof value === 'number' ? value.toFixed(SCALE) : value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(str)) {
    throw new Error(`Invalid monetary value: "${value}"`);
  }

  const negative = str.startsWith('-');
  const [whole = '0', fraction = ''] = str.replace('-', '').split('.');
  // Pad or round the fractional part to exactly 2 places (half-up).
  const padded = fraction.padEnd(SCALE + 1, '0');
  let cents = BigInt(whole) * SCALE_FACTOR + BigInt(padded.slice(0, SCALE));
  if (Number(padded[SCALE]) >= 5) cents += 1n;

  return negative ? -cents : cents;
}

/** Renders integer cents back to a fixed-2 decimal string. */
export function fromCents(cents: bigint): MoneyString {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const whole = abs / SCALE_FACTOR;
  const fraction = (abs % SCALE_FACTOR).toString().padStart(SCALE, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

export function addMoney(...values: MoneyString[]): MoneyString {
  return fromCents(values.reduce((sum, v) => sum + toCents(v), 0n));
}

export function subtractMoney(a: MoneyString, b: MoneyString): MoneyString {
  return fromCents(toCents(a) - toCents(b));
}

/**
 * premium × percentage / 100, rounded half-up to cents.
 *
 * This mirrors the `commission_amount` generated column in the policies table so the
 * UI can preview a commission before the row is written, and the two can never drift.
 */
export function calculateCommission(
  premium: MoneyString,
  percentage: MoneyString | number,
): MoneyString {
  const premiumCents = toCents(premium);
  // Percentage carries 2 decimals (NUMERIC(5,2)), so scale by 10_000 then divide.
  const pctScaled = toCents(percentage);
  const numerator = premiumCents * pctScaled;
  const denominator = 100n * SCALE_FACTOR;
  return fromCents(divideRoundHalfUp(numerator, denominator));
}

/** Splits an amount by a percentage share (used for producer commission splits). */
export function percentageOf(amount: MoneyString, percentage: MoneyString | number): MoneyString {
  return calculateCommission(amount, percentage);
}

function divideRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n !== denominator < 0n;
  const absN = numerator < 0n ? -numerator : numerator;
  const absD = denominator < 0n ? -denominator : denominator;
  const quotient = absN / absD;
  const remainder = absN % absD;
  const rounded = remainder * 2n >= absD ? quotient + 1n : quotient;
  return negative ? -rounded : rounded;
}

/**
 * Splits a total into `count` instalments whose sum is exactly the total.
 * Remainder cents are pushed onto the first instalment, which is how insurers
 * present a `πρώτη δόση` that is a cent or two larger than the rest.
 */
export function splitIntoInstallments(total: MoneyString, count: number): MoneyString[] {
  if (count < 1) throw new Error('Instalment count must be at least 1');
  const totalCents = toCents(total);
  const base = totalCents / BigInt(count);
  const remainder = totalCents - base * BigInt(count);
  return Array.from({ length: count }, (_, i) => fromCents(i === 0 ? base + remainder : base));
}

/* ── pro-rata cancellation (το «ακυρωτικό») ─────────────────────────────────── */

export interface ProRataCancellation {
  totalDays: number;
  usedDays: number;
  earnedPremium: MoneyString;
  unearnedPremium: MoneyString;
  /** Commission the insurer claws back on the unearned part of the ΚΑΘΑΡΑ, at the policy's
   *  rate. Zero when the policy has no καθαρά recorded. */
  commissionClawback: MoneyString;
}

/**
 * Splits a period's premium into earned and unearned at a mid-term cancellation —
 * the number a Greek office calls the ακυρωτικό and today computes by hand.
 *
 * Cover runs noon to noon, so a period lasts exactly `end − start` days (28/03 → 28/09 is 184)
 * and a cancellation effective on day X has used `X − start` of them.
 * The earned share is floored to the cent, so the rounding cent always lands on the
 * client's refund rather than on the agency's side — the defensible direction when a
 * cent has to go somewhere.
 */
export function prorateCancellation(
  premium: MoneyString,
  netPremium: MoneyString | null | undefined,
  commissionPercentage: MoneyString | number,
  startDate: IsoDate,
  endDate: IsoDate,
  effectiveDate: IsoDate,
): ProRataCancellation {
  const totalDays = diffInDays(startDate, endDate);
  if (totalDays <= 0) throw new Error('Policy period is empty');

  const usedDays = Math.min(Math.max(diffInDays(startDate, effectiveDate), 0), totalDays);

  const premiumCents = toCents(premium);
  const earnedCents = (premiumCents * BigInt(usedDays)) / BigInt(totalDays);
  const earnedPremium = fromCents(earnedCents);
  const unearnedPremium = fromCents(premiumCents - earnedCents);

  /*
   * The refund follows the ΟΛΙΚΑ and the clawback the ΚΑΘΑΡΑ, prorated independently.
   *
   * They are different money: the client gets back the unused share of what they paid, tax
   * included, while the carrier takes back the unused share of the commission — and commission
   * was only ever earned on the net. Prorating one and deriving the other from it would put the
   * tax into the clawback.
   *
   * No καθαρά recorded means a clawback of zero, matching the `commission_amount` generated
   * column: there is no commission on this policy to take back until the figure is filled in.
   */
  const netCents = netPremium ? toCents(netPremium) : 0n;
  const unearnedNet = fromCents(netCents - (netCents * BigInt(usedDays)) / BigInt(totalDays));

  return {
    totalDays,
    usedDays,
    earnedPremium,
    unearnedPremium,
    commissionClawback: calculateCommission(unearnedNet, commissionPercentage),
  };
}

/** Locale-aware display formatting. Presentation only — never feed this back into math. */
export function formatMoney(
  value: MoneyString | number | null | undefined,
  locale = 'el-GR',
  currency = 'EUR',
): string {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
}

/** Compact form for KPI tiles: €124.5K, €1.2M. */
export function formatMoneyCompact(
  value: MoneyString | number | null | undefined,
  locale = 'el-GR',
  currency = 'EUR',
): string {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numeric);
}
